// libs
import React from 'react';
import PropTypes from 'prop-types';
import {
    VictoryLine,
    VictoryAxis,
    VictoryScatter,
    VictoryArea,
    VictoryTooltip,
    VictoryClipContainer
} from 'victory';
import VictoryChart from './IEVictoryChart';
// shared components
import GraphGoalLabel  from './goal-label';
import XAxisLabel  from './x-axis-label';
import XAxisPoints from './x-axis-points';
import XAxisTicks from './x-axis-ticks';
import ZeroLabel  from './zero-label';
import Keys from './keys';
import {
    getXTickValues,
    getLineValues,
    getTrendValues,
    getMaxValue,
    getAreaValues,
    getMinValue,
    getYDomainValues,
    getToolTipValue
} from  './utils';
import * as constants from '@biw/contests-constants';

export class ProgressGraph extends React.Component {

    static propTypes = {
        locale: PropTypes.shape( {} ),
        goal: PropTypes.number,
        lastDay: PropTypes.number,
        programViewStatus: PropTypes.string,
        calculationType: PropTypes.string,

        goalPaceLabel: PropTypes.string,
        trendingKeyLabel: PropTypes.string,
        progressKeyLabel: PropTypes.string,
        goalLineLabel: PropTypes.string,
        xAxisLabel: PropTypes.string,

        goalLabel: PropTypes.shape( {
            labelText: PropTypes.string
        } ),

        days: PropTypes.array,
        onTrackDay: PropTypes.shape( {} ),
        baseDay: PropTypes.shape( {} ),

        primaryColor: PropTypes.string,
    }

    static defaultProps = {
        xAxisLabel: 'Time in Days',
        baseDay: null,
        onTrackDay: [],
        days: []
    }
    render() {
        const {
            locale,
            goalLabel,
            goal,
            lastDay,
            goalPaceLabel,
            goalLineLabel,
            trendingKeyLabel,
            progressKeyLabel,
            programViewStatus,
            calculationType,
            days,
            xAxisLabel,
            primaryColor
        } = this.props;

        if ( days.length === 0 ) {
            return null;
        }

        const showTrend = programViewStatus !== constants.PVS__FINAL_ISSUANCE;

        const onTrackDay = showTrend ? this.props.onTrackDay : [];
        const baseDay = this.props.baseDay || days[0];

        days.unshift( baseDay );

        const trendValues = getTrendValues( days, onTrackDay );
        const xMaxValue = getMaxValue( [ { day: lastDay }, trendValues, days, onTrackDay ], 'day' );

        const goalValues = getLineValues( 0, xMaxValue, goal );

        const yMaxValue = getMaxValue( [ goalValues, trendValues, days, onTrackDay ], 'value' );
        const yMinValue = getMinValue( [ goalValues, trendValues, days, onTrackDay, [ { value: 0 } ] ], 'value' );

        const yDomainValues = getYDomainValues( yMinValue, yMaxValue );
        const xTickValues = getXTickValues( xMaxValue );

        const xAxisValues = getLineValues( 0, xMaxValue,  yDomainValues[ 0 ] );
        const zeroLineValues = [ { day: 0, value: 0 }, { day: xMaxValue, value: 0 } ];

        const goalPace = [ baseDay, { day: xMaxValue, value: goal } ];
        const areaValues = getAreaValues( days, onTrackDay, calculationType, programViewStatus );
        const flyoutStyle = { stroke: 'transparent', strokeWidth: 1, fill: primaryColor };
        const tooltipStyle = {
            fontFamily: '"Roboto Slab", "Roboto", sans-serif',
            fontWeight: 700,
            color: '#FFFFFF',
            fill: '#FFFFFF',
            stroke: 'transparent'

        };
        const letterSpacing = 'normal';

        const baseLabelStyles = {
            fontFamily: '"Roboto Slab", "Roboto", sans-serif',
            fontWeight: 400,
            fontSize: '16px',
            letterSpacing,
            fill: '#353a40',
            stroke: 'transparent',
            textTransform: 'uppercase'
        };
        return (
            <div className="progress-graph">
                    <VictoryChart
                        width={ 732 }
                        height={ 480 }
                        padding={ { left:110, right: 65, top: 30, bottom: 65 } }
                        domainPadding={ { x: 0, y: 0 } }
                        >
                        <VictoryArea
                            data={ areaValues }
                            x="day"
                            y="value"
                            standalone={ false }

                            style={ {
                                data: {
                                    fill: '#f3f3f3',
                                    stroke: '#f3f3f3'
                                }
                            }}
                        />
                        <VictoryAxis
                            domain={ yDomainValues }
                            dependentAxis
                            offsetX={ 65 }
                            padding={ 90 }
                            orientation="left"
                            fixLabelOverlap={true}
                            label={ goalLabel.labelText }
                            style={ { axis: {
                                strokeWidth: 0
                            },
                            axisLabel: {
                                fontFamily: '"Roboto", sans-serif',
                                fontSize: '13px',
                                fontWeight: 700,
                                fill: '#353a40',
                                textTransform: 'uppercase'
                            },
                            grid: {
                                stroke: '#cecece',
                                strokeWidth: 1,
                            },
                            ticks: {
                                fill: 'transparent',
                                size: 1,
                                stroke: 'transparent'
                            },
                            tickLabels: Object.assign( {}, baseLabelStyles, {
                                textAnchor: 'start',
                                x: 0
                            } ) } }
                        />
                        <VictoryLine
                            data={ xAxisValues }
                            x="day"
                            y="value"
                            standalone={ false }
                            style={ {
                                data: {
                                    stroke: '#cecece',
                                    strokeWidth: 2.5
                                }
                            } }
                        />
                        <VictoryLine
                            labelComponent={ <GraphGoalLabel label={goalLineLabel}/> }
                            data={ goalValues }
                            x="day"
                            y="value"
                            labels={[ goalLineLabel ]}
                            style={ {
                                data: {
                                    stroke: '#996300',
                                    strokeWidth: 2.5,
                                    fontFamily: '"Roboto", sans-serif',
                                },
                                labels: {
                                    fill: '#996300'
                                }
                            } }
                            standalone={ false }
                        />
                        <VictoryLine
                            data={ days }
                            x="day"
                            y="value"

                            standalone={ false }
                            style={ {  data: {
                                stroke: '#353a40',
                                strokeWidth: 2.5
                            },
                            labels: Object.assign( {},
                                baseLabelStyles, {
                                    textAnchor: 'middle'
                            }  ) } }
                        />
                        <VictoryAxis
                            tickValues={ xTickValues }
                            label={ xAxisLabel }
                            standalone={ false }
                            tickFormat={ String }
                            style={ {
                                axis: {
                                    strokeWidth: 0
                                },
                                axisLabel: {
                                    fontFamily: '"Roboto", sans-serif',
                                    fontWeight: 700,
                                    fill: '#353a40',
                                    textAnchor: 'middle',
                                    padding: 5,
                                    fontSize: '12px'
                                },
                                grid: {
                                    stroke: '#cecece',
                                    strokeWidth: 0
                                },
                                tickLabels: baseLabelStyles,
                                ticks: {
                                    size: 3,
                                    stroke: '#353a40',
                                    strokeWidth: 1
                                }
                            }}
                            tickLabelComponent={ <XAxisTicks/> }
                            tickComponent={ <XAxisPoints/> }
                            axisLabelComponent={ <XAxisLabel/> }
                        />
                        { yMinValue < 0 &&
                            <VictoryLine
                                labelComponent={ <ZeroLabel/> }
                                labels={[ '0' ]}
                                data={ zeroLineValues }
                                x="day"
                                y="value"
                                standalone={ false }
                                style={ { data: {
                                    stroke: '#cecece',
                                    strokeWidth: 1,
                                    strokeDasharray: '5, 3'
                                } } }
                                groupComponent={ <VictoryClipContainer /> }
                            />
                        }
                        { calculationType !== 'average' && calculationType !== 'reduction' &&
                            <VictoryLine
                                data={ goalPace }
                                x="day"
                                y="value"
                                standalone={ false }
                                style={ {
                                    data: {
                                        stroke: '#cecece',
                                        strokeWidth: 2.5
                                    }
                                } }
                            />
                        }
                        { programViewStatus !== constants.PVS__FINAL_ISSUANCE &&
                            <VictoryLine
                                data={ trendValues }
                                x="day"
                                y="value"
                                standalone={ false }
                                style={ {
                                    data: {
                                        stroke: '#353a40',
                                        strokeWidth: 2.5,
                                        strokeDasharray: '5, 3'
                                    }
                                } }
                            />
                        }
                        <VictoryScatter
                            data={ days }
                            x="day"
                            y="value"
                            date="date"
                            size={ 5 }
                            standalone={ false }
                            style={ { data: {
                                display: ( d ) => d.x === 0 ? 'none' : 'block',
                                fill: '#f3f3f3',
                                stroke: '#353a40',
                                strokeWidth: 2.5
                            } } }
                            labels={ d => getToolTipValue( d, goalLabel, locale ) }
                            labelComponent={ <VictoryTooltip
                                style={ tooltipStyle }
                                flyoutStyle={ flyoutStyle }
                        />  }
                    />

                    </VictoryChart>
                    <Keys
                        showTrend={ showTrend }
                        progressKeyLabel={ progressKeyLabel }
                        trendingKeyLabel={ trendingKeyLabel }
                        goalPaceLabel={ goalPaceLabel }
                        calculationType={ calculationType } />
                </div>
        );
    }
}

export default ProgressGraph;
